import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// ── Crop, before a picture is sent (report 1e817873) ─────────────────────────────────────────────────────────────
// The reporter: "Allow users to crop images in the drop folder chat app", and, asked which pictures: "An image that is
// loaded via paperclip or copy and paste to any group or chat should have a crop tool allowing them to remove / keep
// any section/part of the image." Rotate and Rectify (AI) only ever worked on a photo already posted; a picture still
// on its way had no tool at all.
// This is that tool: the picture with a box over it and everything outside the box darkened. The box is carried by
// its middle and sized by its four corners and its four sides — with a mouse, a finger or a pen, as the Rectify (AI)
// window's corners are (EmailDetail/RectifyModal, whose measure and pointer capture this borrows) — and a new box is
// drawn by dragging across the picture outside it. "Keep this part" cuts the picture down to the box here in the
// browser: nothing goes to the server until the picture is sent, so nobody in the chat ever sees what was cut away.
// Cancel, × and Esc leave the picture exactly as it was.
// The chat opens it from the strip of pictures waiting above the typing box (pasted, or picked with the paperclip),
// from the large preview of one of those, and from the photo waiting to be named in the document-upload group,
// before Confirm (ChatPanel).

/** A part of the picture, as fractions of it: from the left, from the top, how wide, how tall. */
type Cut = { x: number; y: number; w: number; h: number };
const WHOLE: Cut = { x: 0, y: 0, w: 1, h: 1 };
/** A box that still takes in the whole picture, which cuts nothing away. */
const isWhole = (c: Cut): boolean => c.x <= 0.0005 && c.y <= 0.0005 && c.w >= 0.9995 && c.h >= 0.9995;
// What a press takes hold of: the box itself, one of its sides or corners (by the compass), or the picture outside the
// box, which draws a new one.
type Part = 'move' | 'draw' | 'n' | 's' | 'e' | 'w' | 'nw' | 'ne' | 'sw' | 'se';
const RED = '#E24B4A';
// A corner handle: the circle that shows, and the square a finger or a mouse may press to take hold of it — the
// Rectify (AI) window's own two sizes. A side is taken hold of anywhere along it, in a band EDGE wide.
const DOT = 13;
const GRAB = 30;
const EDGE = 16;
// The box is never made smaller than this on screen, so its corners never pile up on top of one another. A smaller
// part is had by cropping twice: the second time the cut-down picture fills the window.
const MIN_PX = 40;
// Over the large preview (10300) and its buttons (10301) it may be opened from, as the chat's Rectify (AI) window is.
const Z = 10310;
// The most pixels a cut-out is written with: an iPhone's browser draws nothing on a canvas bigger than this, so a part
// of a very large photo comes back a little smaller instead of not at all.
const MAX_AREA = 16_777_216;

// The four sorts of picture the chat draws and names (its PASTE_EXT), each with the endings a file of that sort goes by.
const ENDINGS: Record<string, string[]> = {
  'image/png': ['png'], 'image/jpeg': ['jpg', 'jpeg', 'jpe', 'jfif'], 'image/gif': ['gif'], 'image/webp': ['webp'],
};
/** Which of the four a file is — by the type it came with, or by its name's ending where it came with none — and ''
 *  for anything else, which this window does not cut. */
function sortOf(type: string, name: string): string {
  const ct = String(type || '').toLowerCase().split(';')[0].trim();
  if (ENDINGS[ct]) return ct;
  if (ct === 'image/jpg' || ct === 'image/pjpeg') return 'image/jpeg';
  if (ct && ct !== 'application/octet-stream') return '';
  const ext = (/\.([a-z0-9]+)$/i.exec(name || '')?.[1] || '').toLowerCase();
  return Object.keys(ENDINGS).find(k => ENDINGS[k].includes(ext)) || '';
}
/** A picture this window can crop: a PNG, a JPEG, a GIF or a WebP. */
export const canCrop = (type: string, name: string): boolean => !!sortOf(type, name);
/** What a cut-out is written as: a JPEG stays a JPEG and a WebP a WebP; a PNG, and the still a GIF becomes, a PNG. */
const writeAs = (sort: string): string => (sort === 'image/jpeg' || sort === 'image/webp' ? sort : 'image/png');
/** The name a cut-out goes by: the picture's own, unless its ending names another sort than the copy came out as. */
function nameFor(name: string, type: string): string {
  const ends = ENDINGS[type];
  const m = /\.([a-z0-9]+)$/i.exec(name || '');
  if (!ends || (m && ends.includes(m[1].toLowerCase()))) return name;
  return `${m ? name.slice(0, m.index) : name}.${ends[0]}`;
}

// The picture read the right way up, as the <img> on screen draws it — a phone's photo is often stored on its side,
// with a note of which way to turn it. TypeScript 4.9's DOM types list only this option's older values, hence the
// cast; a browser that knows only those refuses 'from-image' and is asked again with its own default, and cutOut
// checks the shape of what comes back against the picture on screen.
const UPRIGHT = { imageOrientation: 'from-image' } as unknown as ImageBitmapOptions;
async function upright(blob: Blob): Promise<ImageBitmap> {
  try { return await createImageBitmap(blob, UPRIGHT); }
  catch { return createImageBitmap(blob); }
}

/** The part of `blob` inside `cut`, written out as a picture of `type` — or null when the part is every pixel of it,
 *  which leaves nothing to cut away. `shown` is the picture on screen, the one the box was drawn on: where the
 *  browser's own copy of the bytes comes out another shape than it (a photo's turn read one way there and another
 *  here), or no copy can be had at all, the part is cut from that instead. */
async function cutOut(blob: Blob, cut: Cut, type: string, shown: HTMLImageElement | null): Promise<Blob | null> {
  const seen = !!shown && shown.naturalWidth > 0 && shown.naturalHeight > 0;
  let src: CanvasImageSource | null = null;
  let bmp: ImageBitmap | null = null;
  let w = 0, h = 0;
  try {
    bmp = await upright(blob);
    const ratio = bmp.width / bmp.height;
    if (seen && Math.abs(ratio - shown.naturalWidth / shown.naturalHeight) > ratio * 0.02) { bmp.close(); bmp = null; }
    else { src = bmp; w = bmp.width; h = bmp.height; }
  } catch { bmp = null; /* no copy of its own to be had here: the picture on screen serves */ }
  if (!src) {
    if (!seen) throw new Error('unreadable');
    src = shown;
    w = shown.naturalWidth;
    h = shown.naturalHeight;
  }
  try {
    const sx = Math.round(cut.x * w), sy = Math.round(cut.y * h);
    const sw = Math.max(1, Math.min(w - sx, Math.round((cut.x + cut.w) * w) - sx));
    const sh = Math.max(1, Math.min(h - sy, Math.round((cut.y + cut.h) * h) - sy));
    if (sx === 0 && sy === 0 && sw === w && sh === h) return null;
    const k = Math.min(1, Math.sqrt(MAX_AREA / (sw * sh)));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.floor(sw * k));
    c.height = Math.max(1, Math.floor(sh * k));
    const g = c.getContext('2d');
    if (!g) throw new Error('no canvas');
    g.drawImage(src, sx, sy, sw, sh, 0, 0, c.width, c.height);
    const out = await new Promise<Blob | null>(res => c.toBlob(res, type, 0.92));
    if (!out || !out.size) throw new Error('not written');
    return out;
  } finally {
    if (bmp) bmp.close();
  }
}

const in01 = (v: number) => Math.max(0, Math.min(1, v));
/** From the anchor `a` towards the pointer `p`, along one side of the picture: never shorter than `min`, never off it. */
function span(a: number, p: number, min: number): [number, number] {
  if (p >= a) {
    const hi = Math.min(1, Math.max(p, a + min));
    return [Math.max(0, Math.min(a, hi - min)), hi];
  }
  const lo = Math.max(0, Math.min(p, a - min));
  return [lo, Math.min(1, Math.max(a, lo + min))];
}
/** The box after a drag of (dx, dy) — fractions of the picture — on `part`, worked out from where the box stood when
 *  the press began (`from`). The middle carries the whole box and stops at the picture's edge; a side or a corner moves
 *  its own sides alone and stops short of the sides facing them; the picture outside the box draws a new one from the
 *  point pressed (ax, ay). `mw` and `mh` are the smallest the box may be, as fractions too. */
function reshape(part: Part, from: Cut, dx: number, dy: number, ax: number, ay: number, mw: number, mh: number): Cut {
  if (part === 'move') {
    return { ...from, x: Math.max(0, Math.min(1 - from.w, from.x + dx)), y: Math.max(0, Math.min(1 - from.h, from.y + dy)) };
  }
  if (part === 'draw') {
    const [l, r] = span(in01(ax), in01(ax + dx), mw);
    const [t, b] = span(in01(ay), in01(ay + dy), mh);
    return { x: l, y: t, w: r - l, h: b - t };
  }
  let l = from.x, t = from.y, r = from.x + from.w, b = from.y + from.h;
  if (part.indexOf('w') >= 0) l = Math.max(0, Math.min(r - mw, l + dx));
  if (part.indexOf('e') >= 0) r = Math.min(1, Math.max(l + mw, r + dx));
  if (part.indexOf('n') >= 0) t = Math.max(0, Math.min(b - mh, t + dy));
  if (part.indexOf('s') >= 0) b = Math.min(1, Math.max(t + mh, b + dy));
  return { x: l, y: t, w: r - l, h: b - t };
}

// ── A chat picture's square (report 42100db4) ─────────────────────────────────────────────────────────────────────
// A person's or a group's picture is drawn in a circle, so its box stays square on the picture — as many pixels wide as
// tall — and is sized by its corners alone.
/** The largest square in the middle of a picture `w` × `h` pixels, as fractions of it: where a picture's box starts. */
function middleSquare(w: number, h: number): Cut {
  if (!(w > 0) || !(h > 0)) return WHOLE;
  if (w >= h) { const f = h / w; return { x: (1 - f) / 2, y: 0, w: f, h: 1 }; }
  const f = w / h;
  return { x: 0, y: (1 - f) / 2, w: 1, h: f };
}
/** reshape, for the square. `r` is the picture's width over its height, so a fraction across × r and a fraction down
 *  are one unit (the picture's height); `min` is the smallest side in that unit. The middle carries the box as before;
 *  a corner moves its two sides together with the opposite corner staying put, by the pull along whichever way the
 *  pointer went the further; the picture outside the box draws a new square from the point pressed. Never off the
 *  picture. */
function reshapeSquare(part: Part, from: Cut, dx: number, dy: number, ax: number, ay: number, r: number, min: number): Cut {
  if (part === 'move') return reshape(part, from, dx, dy, ax, ay, 0, 0);
  let fx: number, fy: number, goE: boolean, goS: boolean, want: number;
  if (part === 'draw') {
    fx = in01(ax) * r; fy = in01(ay);
    goE = dx >= 0; goS = dy >= 0;
    want = Math.max(Math.abs(dx * r), Math.abs(dy));
  } else {
    const west = part.indexOf('w') >= 0, north = part.indexOf('n') >= 0;
    fx = (west ? from.x + from.w : from.x) * r; fy = north ? from.y + from.h : from.y;
    goE = !west; goS = !north;
    want = from.h + (Math.abs(dx * r) >= Math.abs(dy) ? (west ? -dx * r : dx * r) : (north ? -dy : dy));
  }
  const room = Math.min(goE ? r - fx : fx, goS ? 1 - fy : fy);
  const s = Math.max(Math.min(min, room), Math.min(room, want));
  return { x: (goE ? fx : fx - s) / r, y: goS ? fy : fy - s, w: s / r, h: s };
}

/**
 * `blob` = the picture as it stands now; `name` its file name, shown under the heading and handed back — with its
 * ending changed only where the cut-out came out as another sort (a GIF becomes a PNG). `onCrop` gets the cut-out and
 * that name; `onCancel` is Cancel, × and Esc, and also a Keep that would cut nothing away.
 * `avatar` = a chat picture (report 42100db4): a square box from the largest square in the middle, corners only, with
 * the circle the picture will be drawn in; and Use is allowed on a square picture left whole, which comes back as it is.
 */
export default function ImageCrop({ blob, name, onCancel, onCrop, avatar = false }:
  { blob: Blob; name: string; onCancel: () => void; onCrop: (cut: Blob, name: string) => void; avatar?: boolean }) {
  const { t } = useTranslation();
  // The picture drawn from the computer's own memory, through an object URL of this window's own that is let go of
  // as the window closes.
  const [src, setSrc] = useState('');
  useEffect(() => {
    const u = URL.createObjectURL(blob);
    setSrc(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  // The box, over the whole picture to begin with: nothing is cut until it has been moved in.
  const [cut, setCut] = useState<Cut>(WHOLE);
  const cutRef = useRef(cut);
  cutRef.current = cut;
  const [working, setWorking] = useState(false);
  const [err, setErr] = useState('');
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // Escape is caught on the way DOWN and stopped there, as the Rectify (AI) window catches it: the Chat window and the
  // large preview this may stand over listen for Escape too, and without this they would close underneath it instead.
  // (The preview stands down while [data-crop] is on screen.)
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      cancelRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);
  // The keys come to this window while it is open, not to the typing box behind it, where Enter would send.
  const shell = useRef<HTMLDivElement | null>(null);
  useEffect(() => { shell.current?.focus(); }, []);

  // ── Where the picture is drawn (the Rectify (AI) window's own measure) ─────────────────────────────────────────
  // The <img> fills its gray ground bar a 12px inset and holds the picture centred in that by objectFit: contain, so
  // the box goes by the PICTURE's rectangle, not the <img>'s — or a wide screenshot in a tall window would have its
  // box out on the gray. Measured again whenever the ground changes size (the window follows the browser's).
  const groundRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [box, setBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const boxRef = useRef(box);
  boxRef.current = box;
  const measure = useCallback(() => {
    const el = groundRef.current, im = imgRef.current;
    if (!el || !im || !im.naturalWidth || !im.naturalHeight) return;
    const gr = el.getBoundingClientRect(), ir = im.getBoundingClientRect();
    const s = Math.min(ir.width / im.naturalWidth, ir.height / im.naturalHeight);
    const w = im.naturalWidth * s, h = im.naturalHeight * s;
    const next = { x: ir.left - gr.left + (ir.width - w) / 2, y: ir.top - gr.top + (ir.height - h) / 2, w, h };
    setBox(p => (p && Math.abs(p.x - next.x) < 0.5 && Math.abs(p.y - next.y) < 0.5
      && Math.abs(p.w - next.w) < 0.5 && Math.abs(p.h - next.h) < 0.5 ? p : next));
  }, []);
  useLayoutEffect(() => {
    const el = groundRef.current;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);
  // A chat picture's box starts on the largest square in the middle, which the picture's own size decides.
  const squared = useRef(false);
  const loaded = () => {
    const im = imgRef.current;
    if (avatar && !squared.current && im && im.naturalWidth > 0 && im.naturalHeight > 0) {
      squared.current = true;
      setCut(middleSquare(im.naturalWidth, im.naturalHeight));
    }
    measure();
  };

  // ── Taking hold of the box ──────────────────────────────────────────────────────────────────────────────────
  // A press takes hold of a part and it follows the pointer — a mouse, a finger or a pen alike — for as long as it is
  // held, the pointer captured so the drag does not stop at the edge of what was pressed. Each move is worked out
  // afresh from where the box stood when the press began, so the box ends where the pointer ends.
  // While the box still takes in the whole picture it has nowhere to be carried to, so a drag across its middle draws
  // the first box instead — dragging a box over the part to keep works from the very first press.
  const held = useRef<{ part: Part; id: number; x: number; y: number; ax: number; ay: number; from: Cut; moved: boolean } | null>(null);
  const take = (part: Part) => (e: React.PointerEvent<HTMLDivElement>) => {
    const b = boxRef.current, el = groundRef.current;
    if (working || e.button !== 0 || !b || !el || !(b.w > 0) || !(b.h > 0)) return;
    e.preventDefault();
    e.stopPropagation();   // a press on the box or a handle is not a press on the picture, which would draw a new box
    const gr = el.getBoundingClientRect();
    held.current = {
      part: part === 'move' && isWhole(cutRef.current) ? 'draw' : part, id: e.pointerId, x: e.clientX, y: e.clientY,
      ax: (e.clientX - gr.left - b.x) / b.w, ay: (e.clientY - gr.top - b.y) / b.h,
      from: cutRef.current, moved: false,
    };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* the drag still follows the pointer over the picture */ }
  };
  const follow = (e: React.PointerEvent<HTMLDivElement>) => {
    const p = held.current, b = boxRef.current;
    if (!p || e.pointerId !== p.id || !b || !(b.w > 0) || !(b.h > 0)) return;
    // A press that has not moved yet changes nothing, so a click on the picture outside the box keeps the box.
    if (!p.moved && Math.abs(e.clientX - p.x) < 3 && Math.abs(e.clientY - p.y) < 3) return;
    p.moved = true;
    setCut(avatar
      ? reshapeSquare(p.part, p.from, (e.clientX - p.x) / b.w, (e.clientY - p.y) / b.h, p.ax, p.ay, b.w / b.h, MIN_PX / b.h)
      : reshape(p.part, p.from, (e.clientX - p.x) / b.w, (e.clientY - p.y) / b.h, p.ax, p.ay,
        Math.min(1, MIN_PX / b.w), Math.min(1, MIN_PX / b.h)));
  };
  const letGo = (e: React.PointerEvent<HTMLDivElement>) => {
    if (held.current && e.pointerId === held.current.id) held.current = null;
  };

  // ── Keep this part ──────────────────────────────────────────────────────────────────────────────────────────
  // The picture cut down to the box and handed back to the chat, which puts it in the place of the one it was cut
  // from. Dead while the box still takes in the whole picture: there is nothing to cut away yet.
  // A chat picture may be used whole: a square photo already is the square.
  const whole = isWhole(cut);
  const keepOff = working || (whole && !avatar) || !box;
  const keep = async () => {
    if (keepOff) return;
    const type = writeAs(sortOf(blob.type, name));
    setWorking(true);
    setErr('');
    try {
      const out = await cutOut(blob, cutRef.current, type, imgRef.current);
      if (!alive.current) return;
      if (!out && avatar) { onCrop(blob, name); return; }   // a chat picture left whole is used as it is
      if (!out) { onCancel(); return; }   // the box took in every pixel after all: the picture stays as it was
      onCrop(out, nameFor(name, out.type || type));
    } catch {
      if (alive.current) setErr(t('chat_crop_failed'));
    } finally {
      if (alive.current) setWorking(false);
    }
  };

  const button = (filled: boolean, off: boolean): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)',
    border: filled ? 'none' : '1px solid var(--border)', background: filled ? 'var(--accent)' : 'transparent',
    color: filled ? '#fff' : 'var(--text-secondary)', cursor: off ? 'default' : 'pointer', opacity: off ? 0.6 : 1, flexShrink: 0,
  });

  // The box and its eight handles, where the picture is drawn — read from `box`, which is null until it is measured.
  // The dark over what will be cut away and the box's edge are one drawing that takes no presses itself: the middle
  // of the box, its sides and its corners each take their own, the corners on top.
  const L = box ? box.x + cut.x * box.w : 0, T = box ? box.y + cut.y * box.h : 0;
  const W = box ? cut.w * box.w : 0, H = box ? cut.h * box.h : 0;
  const corner = (part: Part, cx: number, cy: number, cursor: string) => (
    <div key={part} onPointerDown={take(part)}
      style={{ position: 'absolute', left: cx - GRAB / 2, top: cy - GRAB / 2, width: GRAB, height: GRAB, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor }}>
      <div style={{ width: DOT, height: DOT, borderRadius: '50%', background: 'var(--accent)', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );
  // A side: the band along it between the two corners, with a short bar in its middle to show it can be taken hold of.
  const side = (part: Part, left: number, top: number, across: boolean) => (
    <div key={part} onPointerDown={take(part)}
      style={{
        position: 'absolute', left, top, width: across ? Math.max(0, W - GRAB) : EDGE, height: across ? EDGE : Math.max(0, H - GRAB),
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: across ? 'ns-resize' : 'ew-resize',
      }}>
      <div style={{ width: across ? 18 : 6, height: across ? 6 : 18, borderRadius: 3, background: 'var(--accent)', border: '1.5px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
    </div>
  );

  return (
    <div data-crop style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: Z, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'min(24px, 3vw)' }}>
      <div ref={shell} role="dialog" aria-modal="true" aria-label={t(avatar ? 'chat_pic_set' : 'chat_crop')} tabIndex={-1}
        style={{ width: 'min(900px, 100%)', height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', overflow: 'hidden', outline: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px 12px 18px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <i className={`ti ${avatar ? 'ti-user-circle' : 'ti-crop'}`} style={{ fontSize: 20, color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{t(avatar ? 'chat_pic_set' : 'chat_crop')}</div>
            <div title={name} style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
          </div>
          <button onClick={onCancel} title={t('cancel')} aria-label={t('cancel')}
            style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-x" style={{ fontSize: 15 }} aria-hidden />
          </button>
        </div>

        {/* The picture on its gray ground. A press anywhere on the ground outside the box draws a new one; the ground
            takes the whole drag, so a finger moving over it never scrolls or zooms the page instead. */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', padding: '14px 18px' }}>
          <div ref={groundRef} onPointerDown={take('draw')} onPointerMove={follow} onPointerUp={letGo} onPointerCancel={letGo} onLostPointerCapture={letGo}
            style={{ flex: 1, minWidth: 0, position: 'relative', background: 'var(--c-gray-200)', borderRadius: 'var(--radius-md)', overflow: 'hidden', touchAction: 'none', userSelect: 'none', WebkitUserSelect: 'none', cursor: 'crosshair' }}>
            {src && (
              <img ref={imgRef} src={src} alt={name} draggable={false} onLoad={loaded} onError={() => setErr(t('chat_crop_failed'))}
                style={{ position: 'absolute', left: 12, top: 12, width: 'calc(100% - 24px)', height: 'calc(100% - 24px)', objectFit: 'contain', pointerEvents: 'none' }} />
            )}
            {box && (
              <>
                <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} aria-hidden>
                  <path fillRule="evenodd" fill="rgba(0,0,0,0.55)"
                    d={`M${box.x} ${box.y}h${box.w}v${box.h}h${-box.w}z M${L} ${T}h${W}v${H}h${-W}z`} />
                  {/* A white line under the green one, so the edge shows over a dark picture as well as a pale one. */}
                  {[{ c: '#fff', w: 3.5, o: 0.8 }, { c: 'var(--accent)', w: 1.5, o: 1 }].map((line, i) => (
                    <rect key={i} x={L} y={T} width={W} height={H} fill="none" style={{ stroke: line.c }} strokeOpacity={line.o} strokeWidth={line.w} />
                  ))}
                  {/* A chat picture: the circle it will be drawn in, dashed, inside the square. */}
                  {avatar && <circle cx={L + W / 2} cy={T + H / 2} r={Math.max(0, Math.min(W, H) / 2)} fill="none" stroke="#fff" strokeWidth={1.5} strokeDasharray="6 4" />}
                </svg>
                <div onPointerDown={take('move')} style={{ position: 'absolute', left: L, top: T, width: W, height: H, cursor: working ? 'default' : whole ? 'crosshair' : 'move' }} />
                {/* A chat picture's square is sized by its corners alone: a side would pull it out of square. */}
                {!avatar && side('n', L + GRAB / 2, T - EDGE / 2, true)}
                {!avatar && side('s', L + GRAB / 2, T + H - EDGE / 2, true)}
                {!avatar && side('w', L - EDGE / 2, T + GRAB / 2, false)}
                {!avatar && side('e', L + W - EDGE / 2, T + GRAB / 2, false)}
                {corner('nw', L, T, 'nwse-resize')}
                {corner('ne', L + W, T, 'nesw-resize')}
                {corner('sw', L, T + H, 'nesw-resize')}
                {corner('se', L + W, T + H, 'nwse-resize')}
              </>
            )}
          </div>
        </div>

        <div style={{ flexShrink: 0, padding: '12px 18px 14px', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {err && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: RED }}>
              <i className="ti ti-alert-circle" style={{ fontSize: 14, flexShrink: 0 }} aria-hidden />{err}
            </div>
          )}
          {/* The two buttons keep together: on a phone they wrap under the hint as one row. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--text-secondary)' }}>{t(avatar ? 'chat_pic_crop_hint' : 'chat_crop_hint')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginLeft: 'auto' }}>
              <button onClick={onCancel} style={button(false, false)}>{t('cancel')}</button>
              <button onClick={() => void keep()} disabled={keepOff} style={button(true, keepOff)}>
                <i className={`ti ${working ? 'ti-loader-2' : avatar ? 'ti-check' : 'ti-crop'}`} style={{ fontSize: 13, animation: working ? 'spin 1s linear infinite' : undefined }} aria-hidden />
                {t(avatar ? 'chat_pic_use' : 'chat_crop_keep')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
